/**
 * Manufacturer PCN access comparison matrix (server-rendered, all rows in HTML).
 *
 * Nuanced status strings (never forced Yes/No); "Not clearly documented" renders
 * muted. Manufacturer names link to the detailed section below. On mobile the
 * table scrolls horizontally with a visible hint; a legend explains the statuses.
 */
import { formatReviewDate } from "./section";
import type { ManufacturerAccessEntry } from "@/lib/seo/content/types";

function Cell({ value }: { value: string }) {
  const muted = value === "Not clearly documented";
  // Full text stays in the DOM (SEO + the detailed section repeats it); the
  // matrix just clamps the display to keep rows tidy. `title` shows the rest on
  // hover.
  return (
    <span
      title={value}
      className={"block line-clamp-3 " + (muted ? "ink-3 italic" : "ink-2")}
    >
      {value}
    </span>
  );
}

export function ManufacturerMatrix({
  manufacturers,
  intakeLabel = "Email + PDF upload",
  note,
}: {
  manufacturers: ManufacturerAccessEntry[];
  intakeLabel?: string;
  note?: string;
}) {
  const headers = [
    "Manufacturer",
    "Public archive",
    "Subscription",
    "Account",
    "Customer-specific",
    "Distributor",
    "PCNshark intake",
    "Last verified",
  ];
  return (
    <div>
      <p className="lg:hidden font-mono text-[10px] uppercase tracking-wider ink-3 mb-2">
        Swipe to compare →
      </p>
      <div className="overflow-x-auto border rule">
        <table className="min-w-[860px] w-full border-collapse text-left bg-white">
          <caption className="sr-only">
            How major manufacturers distribute PCNs, and how PCNshark ingests them
          </caption>
          <thead>
            <tr className="border-b rule bg-paper-2">
              {headers.map((h, i) => (
                <th
                  key={h}
                  scope="col"
                  className={
                    "px-4 py-3 font-mono text-[10px] uppercase tracking-wider font-normal align-bottom " +
                    (i === 6 ? "text-[color:var(--shark-accent)]" : "ink-3")
                  }
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {manufacturers.map((m) => (
              <tr key={m.id} className="border-b rule last:border-b-0 align-top">
                <th scope="row" className="px-4 py-4 text-left">
                  <a
                    href={`#${m.id}`}
                    className="font-display font-semibold text-[13.5px] ulink hover:text-[color:var(--shark-accent-ink)]"
                  >
                    {m.name}
                  </a>
                </th>
                <td className="px-4 py-4 text-[12.5px] leading-snug">
                  <Cell value={m.facts.publicArchive} />
                </td>
                <td className="px-4 py-4 text-[12.5px] leading-snug">
                  <Cell value={m.facts.emailNotification} />
                </td>
                <td className="px-4 py-4 text-[12.5px] leading-snug">
                  <Cell value={m.facts.accountRequired} />
                </td>
                <td className="px-4 py-4 text-[12.5px] leading-snug">
                  <Cell value={m.facts.customerSpecific} />
                </td>
                <td className="px-4 py-4 text-[12.5px] leading-snug">
                  <Cell value={m.facts.distributorInvolvement} />
                </td>
                <td className="px-4 py-4 text-[12.5px] leading-snug ink-2">
                  {intakeLabel}
                </td>
                <td className="px-4 py-4 text-[12px] leading-snug ink-3 whitespace-nowrap font-mono">
                  {formatReviewDate(m.lastVerified)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {note && (
        <p className="mt-3 font-mono text-[11px] ink-3 leading-relaxed max-w-[90ch]">
          {note}
        </p>
      )}
      <p className="mt-2 font-mono text-[11px] ink-3 leading-relaxed max-w-[90ch]">
        Statuses describe what each manufacturer documents publicly. “Not clearly
        documented” means we could not confirm it from official materials — not
        that it is unavailable. “PCNshark intake” is the same for every
        manufacturer: you can forward notification emails or upload PCN PDFs you
        receive. Automatic external coverage is not guaranteed for any
        manufacturer.
      </p>
    </div>
  );
}
